import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Tag, ArrowRight, FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAITranslate } from "@/hooks/useAITranslate";

export interface GuideArticle {
  id: string;
  slot: number;
  key: string;
  slug: string;
  title: string;
  intro: string;
  body: string;
  duration: string;
  price: string;
}

interface ArticleImage {
  id: string;
  slot: number;
  image_url: string;
  alt_text: string;
}

const GuideArticleCard = ({ article }: { article: GuideArticle }) => {
  const [cover, setCover] = useState<ArticleImage | null>(null);
  // AI-translated editorial fields (cached). Reviews/user-generated text excluded.
  const [tTitle, tIntro] = useAITranslate([article.title, article.intro], "en");
  const [tReadArticle] = useAITranslate(["Read article"], "en");

  useEffect(() => {
    (supabase as any)
      .from("guide_article_images")
      .select("*")
      .eq("article_id", article.id)
      .order("slot", { ascending: true })
      .limit(1)
      .then(({ data }: { data: ArticleImage[] | null }) => {
        if (data && data.length) setCover(data[0]);
      });
  }, [article.id]);

  const href = `/guide/${article.slug}`;
  const imgAlt = cover?.alt_text || tTitle;
  const hasRealImage = !!cover?.image_url;

  return (
    <article
      itemScope
      itemType="https://schema.org/Article"
      className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
    >
      <Link to={href} aria-label={tTitle} className="block relative overflow-hidden aspect-video bg-muted">
        {hasRealImage ? (
          <img
            src={cover!.image_url}
            alt={imgAlt}
            itemProp="image"
            width={600}
            height={338}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        ) : (
          <div className="absolute inset-0 overflow-hidden bg-gradient-to-br from-[#f3ece1] to-[#e8e0d5] flex flex-col items-center justify-center">
            <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,_#d4c4b0_0%,_transparent_70%)]" />
            <div className="relative flex flex-col items-center justify-center gap-3 p-6 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/60 backdrop-blur-sm shadow-sm">
                <FileText className="h-7 w-7 text-[#a08060]" strokeWidth={1.5} />
              </div>
              <span className="font-heading text-sm font-medium text-[#8a7660] leading-snug max-w-[80%]">
                {tTitle}
              </span>
            </div>
          </div>
        )}
      </Link>
      <div className="flex flex-1 flex-col p-5 space-y-3 text-foreground">
        <div className="flex flex-wrap gap-2">
          {article.duration && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              {article.duration}
            </span>
          )}
          {article.price && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 px-2.5 py-0.5 text-xs font-semibold text-primary">
              <Tag className="h-3 w-3" />
              {article.price}
            </span>
          )}
        </div>
        <h3 itemProp="headline" className="font-heading text-lg font-bold text-foreground leading-snug line-clamp-2">
          <Link to={href} className="hover:text-primary transition-colors">
            {tTitle}
          </Link>
        </h3>
        <p itemProp="description" className="font-body text-sm text-muted-foreground leading-relaxed line-clamp-3">
          {tIntro}
        </p>
        <div className="pt-3 mt-auto border-t border-border/60">
          <Link
            to={href}
            className="inline-flex items-center gap-1.5 font-body text-sm font-semibold text-primary hover:gap-2.5 transition-all"
          >
            {tReadArticle} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </article>
  );
};

export default GuideArticleCard;